import React, { useEffect, useRef, useState } from "react";
import { SEALED_NO } from "../../data/sporeGames";

const THRESHOLD = 70;
const BRUSH = 56;

const paintFog = (canvas) => {
  const ctx = canvas.getContext("2d");
  const { width: w, height: h } = canvas;
  ctx.globalCompositeOperation = "source-over";
  ctx.fillStyle = "#0b1610";
  ctx.fillRect(0, 0, w, h);
  for (let i = 0; i < 260; i += 1) {
    const x = Math.random() * w;
    const y = Math.random() * h;
    const r = 4 + Math.random() * 30;
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, "rgba(57,255,20,0.32)");
    g.addColorStop(1, "rgba(57,255,20,0)");
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = "rgba(57,255,20,0.55)";
  ctx.font = "14px 'Share Tech Mono', monospace";
  ctx.textAlign = "center";
  ctx.fillText("CONTAMINATED FILE  //  HOLD AND DRAG TO WIPE", w / 2, h / 2);
};

const toCanvas = (c, e) => {
  const rect = c.getBoundingClientRect();
  return { x: ((e.clientX - rect.left) * c.width) / rect.width, y: ((e.clientY - rect.top) * c.height) / rect.height };
};

export const ScanGame = ({ entry, onWin }) => {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const last = useRef(null);
  const moves = useRef(0);
  const done = useRef(false);
  const [cleared, setCleared] = useState(0);

  useEffect(() => {
    const c = canvasRef.current;
    c.width = wrapRef.current.clientWidth;
    c.height = wrapRef.current.clientHeight;
    paintFog(c);
  }, []);

  const measure = () => {
    const c = canvasRef.current;
    const { data } = c.getContext("2d").getImageData(0, 0, c.width, c.height);
    let clear = 0;
    let total = 0;
    for (let i = 3; i < data.length; i += 64) {
      total += 1;
      if (data[i] < 40) clear += 1;
    }
    const pct = Math.round((clear / total) * 100);
    setCleared(pct);
    if (pct >= THRESHOLD && !done.current) {
      done.current = true;
      c.getContext("2d").clearRect(0, 0, c.width, c.height);
      setCleared(100);
      onWin();
    }
  };

  const wipe = (e) => {
    if (!last.current || done.current) return;
    const c = canvasRef.current;
    const pt = toCanvas(c, e);
    const ctx = c.getContext("2d");
    ctx.globalCompositeOperation = "destination-out";
    ctx.lineWidth = BRUSH;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
    last.current = pt;
    moves.current += 1;
    if (moves.current % 5 === 0) measure();
  };

  const start = (e) => {
    canvasRef.current.setPointerCapture?.(e.pointerId);
    last.current = toCanvas(canvasRef.current, e);
    wipe({ clientX: e.clientX + 1, clientY: e.clientY + 1 });
  };

  const stop = () => {
    last.current = null;
    if (!done.current) measure();
  };

  return (
    <div className="space-y-4" data-testid="scan-game">
      <div ref={wrapRef} className="relative h-72 sm:h-80 rounded-sm border border-slate-800 overflow-hidden bg-[#0E131F]">
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 spore-grid-bg">
          <p className="font-tac text-xs tracking-[0.3em] text-cyan-300/80">CASE FILE {SEALED_NO} // {entry.category.toUpperCase()}</p>
          <p data-testid="scan-hidden-symptom" className="font-display uppercase text-4xl sm:text-6xl font-bold text-[#FF2E63] mt-3 spore-glow-red">
            {entry.symptom}
          </p>
          <p className="font-tac text-xs text-slate-400 mt-3">VERDICT PENDING // INFECTED OR NOT?</p>
        </div>
        <canvas
          ref={canvasRef}
          data-testid="scan-canvas"
          className="absolute inset-0 w-full h-full cursor-crosshair touch-none"
          onPointerDown={start}
          onPointerMove={wipe}
          onPointerUp={stop}
          onPointerLeave={stop}
        />
      </div>
      <div className="flex items-center gap-3 font-tac text-xs text-slate-400">
        <span>EXPOSURE</span>
        <div className="flex-1 h-2 bg-slate-950 border border-slate-800 rounded-full overflow-hidden">
          <div className="h-full bg-cyan-400 transition-[width] duration-300" style={{ width: `${Math.min(100, (cleared / THRESHOLD) * 100)}%` }} />
        </div>
        <span data-testid="scan-cleared-pct" className="text-cyan-300 tabular-nums w-12 text-right">{cleared}%</span>
      </div>
    </div>
  );
};
